import React, { useState } from "react";
import Card from "./Card";
import Modal from "./Modal";
import MonteCarloSection from "./MonteCarloSection";
import { AlertTriangle, RefreshCw } from "lucide-react";
import Button from "./Buttons";

const fmt = (val) => {
    if (val == null) return "—";
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
};

const fmtPct = (val) => {
    if (val == null) return "—";
    return `${(Number(val) * 100).toFixed(1)}%`;
};

// Shared spinner shown in a card slot while Monte Carlo is (re)computing, so every MC-derived
// component (AVI, RPI, distribution) shows a spinner rather than disappearing during a recalc.
const SpinnerBox = ({ className = "h-24" }) => (
    <div className={`flex items-center justify-center ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-transparent border-t-indigo-500 border-r-indigo-300" />
    </div>
);

// Monte Carlo is computed live and owned by VendorDashboard (shared with the AI narrative panel),
// so `mcData` / `loading` / `recalculating` / `onRecalculate` arrive as props and a single
// Recalculate refreshes every MC-derived figure on the page.
const VendorImpactMetrics = ({ mcData, loading, recalculating, onRecalculate, anomalyData, financialSummary, deteriorationForecast, activeRiskCount }) => {
    const [showMethodology, setShowMethodology] = useState(false);

    const fairEAL = financialSummary?.totalExpectedAnnualLoss;
    const hasEAL  = fairEAL != null && Number(fairEAL) > 0;

    const { axarisVendorImpactIndex, ransomwarePredictionIndex,
            calculatedAtUtc, threatSignals } = mcData || {};

    const signals = {
        rw:  threatSignals?.ransomwareListings    ?? null,
        cb:  threatSignals?.credentialBreaches    ?? null,
        esm: threatSignals?.criticalEsmFindings   ?? null,
        rwRisks: threatSignals?.ransomwareRelatedRiskCount ?? null,
    };

    const totalRiskCount = activeRiskCount ?? null;

    const mapToMonteCarloFormat = (mc) => ({
        p5: mc.p5EAL, p50: mc.medianEAL, p75: mc.p75EAL,
        p90: mc.p90EAL ?? null, p95: mc.p95EAL, p99: null,
        mean: mc.meanEAL, standardDeviation: mc.standardDeviation,
        iterationCount: mc.iterations, maxObserved: null,
        risksWithCompleteFinancials: totalRiskCount, riskCount: totalRiskCount,
        isPartialResult: false,
    });

    return (
        <div className="mb-6">

            {/* ── Single row: FAIR EAL · AVI · RPI ──── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">

                {/* FAIR EAL — primary financial number */}
                <Card>
                    <div className="flex items-start justify-between">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Financial Exposure
                        </div>
                        <button
                            onClick={() => setShowMethodology(true)}
                            className="text-xs text-indigo-500 hover:text-indigo-700 underline flex-shrink-0"
                        >
                            How it works
                        </button>
                    </div>
                    {hasEAL ? (
                        <div className="text-2xl font-bold text-emerald-700 leading-tight">
                            {fmt(financialSummary?.scenarioBands?.conservative)} – {fmt(financialSummary?.scenarioBands?.severe)}+
                        </div>
                    ) : (
                        <div className="text-2xl font-bold text-gray-300">—</div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                        Conservative – Severe · FAIR-Aligned estimate
                    </div>
                </Card>

                {/* AVI */}
                {loading ? (
                    <Card><SpinnerBox /></Card>
                ) : mcData ? (
                    <Card className="relative group">
                        {anomalyData?.anomalyDetected && (
                            <div className="absolute top-2 right-2 animate-pulse">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                            </div>
                        )}
                        <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-1">
                            AVI — Axaris Vendor Impact
                        </div>
                        <div className="text-3xl font-bold text-indigo-800">
                            {axarisVendorImpactIndex != null ? `${fmt(axarisVendorImpactIndex)}+` : "—"}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Monte Carlo mean EAL (importance-weighted)</div>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg text-left pointer-events-none">
                            AVI = the mean expected annual loss across the simulated years. The vendor importance multiplier (data sensitivity, access scope, revenue dependency) is applied to every loss input before simulating, so it is already part of this figure.
                        </div>
                    </Card>
                ) : null}

                {/* RPI — Ransomware Prediction Index */}
                {loading ? (
                    <Card><SpinnerBox /></Card>
                ) : mcData && ransomwarePredictionIndex != null ? (
                    <Card className="relative group">
                        <div className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">
                            RPI — Ransomware (90d)
                        </div>
                        <div className="text-3xl font-bold text-orange-800">{fmtPct(ransomwarePredictionIndex)}</div>
                        <div className="text-xs text-gray-400 mt-1">Hybrid risk + threat intelligence</div>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg text-left pointer-events-none">
                            Probability of a ransomware-related event within 90 days. Derived from the FAIR loss-event frequency across the vendor's ransomware-related risks, adjusted upward for elevated current ransomware campaign conditions (AI-accelerated attacks), and combined with live threat-intelligence signals.
                        </div>
                    </Card>
                ) : null}
            </div>

            {/* ── MC distribution + threat signals (spinner while (re)computing) ── */}
            {loading || mcData ? (
                <>

                    {/* ── Predictive Risk Distribution ───────────────── */}
                    <div className="mb-4">
                        {loading ? (
                            <Card><SpinnerBox className="h-40" /></Card>
                        ) : (
                            <MonteCarloSection
                                monteCarlo={mapToMonteCarloFormat(mcData)}
                                fairEAL={fairEAL}
                                fairEALLow={financialSummary?.scenarioBands?.conservative}
                                fairEALHigh={financialSummary?.scenarioBands?.severe}
                                showHeroEAL={false}
                            />
                        )}
                    </div>

                    {/* ── Threat signals + Recalculate (kept visible during recalc) ─── */}
                    {mcData ? (
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 px-4 py-2">
                        <div className="flex items-center flex-wrap gap-4 text-sm">
                            {signals.rw != null && (
                                <span className="text-gray-600">
                                    <span className="font-medium text-red-700">{signals.rw}</span> ransomware listings
                                </span>
                            )}
                            {signals.cb != null && (
                                <span className="text-gray-600">
                                    <span className="font-medium text-orange-700">{signals.cb}</span> credential breaches
                                </span>
                            )}
                            {signals.esm != null && (
                                <span className="text-gray-600">
                                    <span className="font-medium text-purple-700">{signals.esm}</span> critical ESM findings
                                </span>
                            )}
                            {signals.rwRisks != null && signals.rwRisks > 0 && (
                                <span className="text-gray-600">
                                    <span className="font-medium text-blue-700">{signals.rwRisks}</span> RW-related risks
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            {calculatedAtUtc && (
                                <span className="text-xs text-gray-400">
                                    Last computed: {new Date(calculatedAtUtc.endsWith("Z") ? calculatedAtUtc : calculatedAtUtc + "Z").toLocaleString()}
                                </span>
                            )}
                            <Button size="sm" variant="secondary" onClick={onRecalculate} disabled={recalculating}>
                                <RefreshCw className={`h-3 w-3 mr-1 ${recalculating ? "animate-spin" : ""}`} />
                                {recalculating ? "Calculating..." : "Recalculate"}
                            </Button>
                        </div>
                    </div>
                    ) : null}
                </>
            ) : null}

            {/* ── Methodology Modal ───────────────────────────────────── */}
            <Modal isOpen={showMethodology} onClose={() => setShowMethodology(false)} title="How Scoring Works" size="3xl">
                <div className="space-y-5 text-sm text-gray-700">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">Overview</h3>
                        <p>
                            Axaris uses the <strong>FAIR (Factor Analysis of Information Risk)</strong> framework to translate vendor risk findings into dollar-denominated impact estimates.
                            Each risk is scored across a multi-dimensional <strong>consequence profile</strong> (identity compromise, blast radius, recovery complexity, regulatory exposure, and more), so two risks in the same category produce different impacts based on their actual business consequences — not a single category multiplier.
                            The <strong>Financial Exposure (FAIR-Aligned)</strong> figure shown at the top is the authoritative financial number — a FAIR-Aligned point estimate of expected annual loss.
                            Monte Carlo simulation is used for predictive intelligence: forecasting future exposure, confidence intervals, and ransomware probability.
                        </p>
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">Financial Exposure vs MC Mean EAL</h3>
                        <p>
                            The <strong>Expected Annual Loss (EAL)</strong> is a FAIR-Aligned point estimate — clean and conservative, the headline figure for budgeting.
                            The <strong>MC Mean EAL</strong> is the mean across 5,000 simulated years. Both are vendor-importance-weighted (same basis), and the two usually differ: live threat-intelligence probability boosts and the right-tailed loss distribution (rare severe years) pull the MC mean up, while sampling across each input's range rather than its most-likely value can pull it below the point estimate.
                            Both are valid: use the EAL for annual budgets, MC for predictive and stress views.
                        </p>
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">Key Metrics</h3>
                        <table className="w-full text-left border border-gray-200 rounded">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-3 py-2 font-semibold text-gray-900 border-b border-gray-200">Metric</th>
                                    <th className="px-3 py-2 font-semibold text-gray-900 border-b border-gray-200">What It Tells You</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                <tr>
                                    <td className="px-3 py-2 font-medium text-emerald-700">Financial Exposure (FAIR-Aligned)</td>
                                    <td className="px-3 py-2">The authoritative expected annual loss estimate. FAIR-Aligned calculation driven by each risk's consequence profile (AI-informed inputs) — use this for board reporting, insurance sizing, and budget requests.</td>
                                </tr>
                                <tr>
                                    <td className="px-3 py-2 font-medium text-indigo-700">AVI (Axaris Vendor Impact)</td>
                                    <td className="px-3 py-2">MC Mean EAL weighted by vendor importance (data sensitivity × access scope × revenue dependency). Useful for comparing vendors.</td>
                                </tr>
                                <tr>
                                    <td className="px-3 py-2 font-medium text-orange-700">RPI (Ransomware Prediction Index)</td>
                                    <td className="px-3 py-2">Hybrid metric: probability of a ransomware-related event within 90 days — FAIR loss-event frequency, adjusted upward for elevated current ransomware campaign conditions, combined with live threat signals.</td>
                                </tr>
                                <tr>
                                    <td className="px-3 py-2 font-medium text-purple-700">P5 – P95 Distribution</td>
                                    <td className="px-3 py-2">The 90% confidence interval from MC simulation. In 90% of simulated years your loss falls within this range. Use P95 as the planning/insurance threshold.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default VendorImpactMetrics;
